import React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Combobox } from "@/components/ui/combobox";
import { PaginatedCombobox } from "@/utils";
import { cn } from "@/lib/utils";
import type { SnapshotOption, SnapshotPeriod } from "./snapshotHelpers";
import { OverviewScopePicker } from "./OverviewScopePicker";

const PERIOD_OPTIONS: { value: SnapshotPeriod; label: string }[] = [
  { value: "all_time", label: "All time" },
  { value: "monthly", label: "Month" },
  { value: "weekly", label: "Week" },
];

export function OverviewFilters({
  period,
  onPeriodChange,
  snapshots,
  playerId,
  playerLabel,
  segmentationDetailId,
  onSelectPlayer,
  onSelectSegmentation,
  onClearScope,
  allowedSegmentationIds,
  disabled = false,
}: {
  period: SnapshotPeriod;
  onPeriodChange: (period: SnapshotPeriod) => void;
  snapshots: PaginatedCombobox<SnapshotOption, false>;
  playerId: number | null;
  playerLabel?: string | null;
  segmentationDetailId: number | null;
  onSelectPlayer: (playerId: number) => void;
  onSelectSegmentation: (segmentationDetailId: number) => void;
  onClearScope?: () => void;
  allowedSegmentationIds?: Set<number> | null;
  disabled?: boolean;
}) {
  const isAllTime = period === "all_time";
  const currentIndex =
    snapshots.value != null ? snapshots.items.findIndex(item => item.id === snapshots.value?.id) : -1;
  const canGoNewer = !disabled && currentIndex > 0;
  const canGoOlder = !disabled && currentIndex >= 0 && currentIndex < snapshots.items.length - 1;

  const goNewer = () => {
    if (!canGoNewer) return;
    snapshots.onValueChange(snapshots.items[currentIndex - 1], undefined);
  };

  const goOlder = () => {
    if (!canGoOlder) return;
    snapshots.onValueChange(snapshots.items[currentIndex + 1], undefined);
  };

  return (
    <section className="flex flex-col gap-4 font-sans" aria-busy={disabled}>
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-3">
        <div
          className={cn(
            "flex flex-wrap items-center gap-x-4 gap-y-3 transition-opacity",
            disabled && "pointer-events-none opacity-50"
          )}
        >
          <div className="inline-flex rounded-lg bg-muted p-1">
            {PERIOD_OPTIONS.map(option => (
              <button
                key={option.value}
                type="button"
                onClick={() => onPeriodChange(option.value)}
                disabled={disabled}
                className={cn(
                  "cursor-pointer rounded-md px-3 py-1.5 text-sm font-medium transition-all disabled:cursor-not-allowed",
                  period === option.value
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                {option.label}
              </button>
            ))}
          </div>

          {!isAllTime && (
            <div className="flex items-center">
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={goOlder}
                disabled={!canGoOlder}
                aria-label="Previous period"
                className="shrink-0 text-muted-foreground"
              >
                <ChevronLeft className="size-4" />
              </Button>

              <Combobox<SnapshotOption>
                key={period}
                {...snapshots}
                // Non-searchable period picker — don't wire async search from the paginated hook.
                onSearch={undefined}
                itemToStringLabel={item => item.label}
                itemToStringValue={item => String(item.id)}
                renderMenuItem={({ label }) => <span className="font-sans">{label}</span>}
                searchable={false}
                showClear={false}
                hideTrigger
                disabled={disabled}
                className={cn(
                  "w-[10.5rem] shrink-0 border-0 bg-transparent shadow-none ring-0 focus-within:border-transparent focus-within:ring-0 sm:w-[12rem]",
                  period === "weekly" && "w-[14rem] sm:w-[16rem]",
                  "[&_input]:truncate [&_input]:border-0 [&_input]:bg-transparent [&_input]:text-center [&_input]:font-semibold [&_input]:tracking-tight [&_input]:shadow-none [&_input]:ring-0",
                  period === "weekly" ? "[&_input]:text-lg" : "[&_input]:text-xl"
                )}
              />

              <Button
                variant="ghost"
                size="icon-sm"
                onClick={goNewer}
                disabled={!canGoNewer}
                aria-label="Next period"
                className="shrink-0 text-muted-foreground"
              >
                <ChevronRight className="size-4" />
              </Button>
            </div>
          )}
        </div>

        <OverviewScopePicker
          playerId={playerId}
          playerLabel={playerLabel}
          segmentationDetailId={segmentationDetailId}
          onSelectPlayer={onSelectPlayer}
          onSelectSegmentation={onSelectSegmentation}
          onClear={onClearScope}
          allowedSegmentationIds={allowedSegmentationIds}
        />
      </div>
    </section>
  );
}
